import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useQuery } from "@tanstack/react-query";
import { materialPropertyService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { MaterialProperty as MaterialPropertyType } from "/app/src/models";
import { useHandleNavigationError } from "/app/src/helpers/api";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import InfoList from "/app/src/components/generic/components/explorer/infoList";
import ModelItem from "/app/src/components/generic/components/explorer/modelItems";

/**
 * MaterialProperty component.
 * It fetches and displays a material property form.
 */
export default function MaterialProperty() {
  const { t } = useTranslation();
  const { materialPropertyId } = useParams();
  const handleNavigationError = useHandleNavigationError();

  //Get the Single Material Property from URL
  const { data: materialProperty } = useQuery({
    queryKey: ["materialProperty", materialPropertyId],
    queryFn: () => {
      return materialPropertyService.getSingle(materialPropertyId).catch(() => {
        handleNavigationError();
      });
    },
    initialData: { material_property: {} },
    retry: false,
    select: (data: { material_property: MaterialPropertyType }) => {
      return data.material_property;
    },
  });

  const basicInfo = [
    { label: t("translation:name"), value: materialProperty?.name },
    {
      label: t("translation:description"),
      value: materialProperty?.description,
    },
  ];

  const propertyConfig = [
    {
      label: t("translation:serial_number_pick_type"),
      value: materialProperty?.serialNumberPickType,
    },
    {
      label: t("translation:serial_number_put_type"),
      value: materialProperty?.serialNumberPutType,
    },
    {
      label: t("translation:serial_number_mixed"),
      value: materialProperty?.isSerialNumberMixed,
    },
    { label: t("translation:expiry"), value: materialProperty?.expiryDays },
    {
      label: t("translation:lot_number_pick_type"),
      value: materialProperty?.lotPickType,
    },
    {
      label: t("translation:lot_number_put_type"),
      value: materialProperty?.lotPutType,
    },
    {
      label: t("translation:lot_number_mixed"),
      value: materialProperty?.isLotMixed,
    },
    {
      label: t("translation:cycle_count"),
      value: materialProperty?.countingCycle,
    },
    {
      label: t("translation:danger_class"),
      value: materialProperty?.dangerClass,
    },
  ];

  const materialList =
    materialProperty?.materials?.map((material) => ({
      id: material.id,
      name: material.name,
    })) ?? [];

  return (
    <div className="explorer">
      {materialProperty ? (
        <>
          <Helmet>
            <title>{materialProperty.name}</title>
          </Helmet>
          <Breadcrumbs
            items={[
              { label: "Explorer", to: "/explorer" },
              {
                label: "Material Properties",
                to: "/explorer/material_properties",
              },
              { label: materialProperty?.name },
            ]}
          />

          <h1 className="text-[36px] font-medium">{materialProperty?.name}</h1>
          <h2 className="text-[16px] mb-6 text-neutral-smoke">
            Material Property Id: {materialProperty?.id}
          </h2>
          <InfoList header={t("translation:basic_info")} items={basicInfo} />
          <InfoList
            header={t("translation:property_configuration")}
            items={propertyConfig}
          />
          <ModelItem modelName="Materials" items={materialList} />
        </>
      ) : (
        <div />
      )}
    </div>
  );
}
